import { searchService } from "@/modules/hub/features/Search/services/searchService";
import type {
  FetchEventSearchResultsRequest,
  SearchResultEvent,
  EventSearchFilters,
} from "../api/fetchEventSearchResults/types";

const stringToId = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

class EventDirectoryAlgoliaService {
  async searchEvents(params: FetchEventSearchResultsRequest): Promise<{
    events: SearchResultEvent[];
    totalCount: number;
    facets: EventSearchFilters;
  }> {
    await searchService.initialize(false);

    const algoliaFilters: any = {};

    if (params.eventTypeNames && params.eventTypeNames.length > 0) {
      algoliaFilters.eventTypes = params.eventTypeNames;
    }

    if (params.categoryNames && params.categoryNames.length > 0) {
      algoliaFilters.eventCategories = params.categoryNames;
    }

    if (params.tradeNames && params.tradeNames.length > 0) {
      algoliaFilters.trades = params.tradeNames;
    }

    if (params.businessRoleNames && params.businessRoleNames.length > 0) {
      algoliaFilters.businessRoles = params.businessRoleNames;
    }

    if (params.showOnlyTotalAccess) {
      algoliaFilters.isEligibleForLata = true;
    }

    const algoliaPage = (params.page || 1) - 1;
    const hitsPerPage = params.pageSize || 12;
    const useFavoritesKey = params.showFavorites === true;

    const result = await searchService.searchEvents(
      params.searchTerm || "",
      algoliaFilters,
      algoliaPage,
      hitsPerPage,
      useFavoritesKey,
    );

    const events: SearchResultEvent[] = result.hits.map((hit: any) => ({
      id: hit.eventId || parseInt(hit.objectID?.replace("event_", "") || "0"),
      uuid: hit.uuid || "",
      eventCode: hit.eventCode || "",
      eventName: hit.eventName || "",
      eventDescription: hit.eventDescription || "",
      eventPrice: hit.eventPrice || 0,
      isPublished: hit.isPublished || false,
      thumbnailUrl: hit.thumbnail || null,
      eventTypeName: hit.eventType || null,
      eventCategoryNames: hit.eventCategories || [],
      createdAt: hit.createdAt || null,
      viewCount: hit.viewCount || 0,
      isVoucherEligible: hit.isVoucherEligible || false,
      isEligibleForLata: hit.isEligibleForLata || false,
      sessions: (hit.sessions || []).map((s: any) => ({
        name: s.name || "",
        startDate: s.startDate || null,
        endDate: s.endDate || null,
        venue: s.venue || null,
        location: s.location || null,
        isVirtual: s.isVirtual || false,
        instructor: s.instructor || null,
      })),
    }));

    const facets: EventSearchFilters = {
      eventTypes: Object.entries(result.facets?.eventType || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      categories: Object.entries(result.facets?.eventCategories || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      trades: Object.entries(result.facets?.trades || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      businessRoles: Object.entries(result.facets?.businessRoles || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
    };

    return {
      events,
      totalCount: result.total,
      facets,
    };
  }

  async getMetadata(): Promise<EventSearchFilters> {
    await searchService.initialize(false);

    const result = await searchService.searchEvents("", {}, 0, 0);

    return {
      eventTypes: Object.entries(result.facets?.eventType || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      categories: Object.entries(result.facets?.eventCategories || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      trades: Object.entries(result.facets?.trades || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
      businessRoles: Object.entries(result.facets?.businessRoles || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          eventCount: count as number,
        }),
      ),
    };
  }
}

export const eventDirectoryAlgoliaService =
  new EventDirectoryAlgoliaService();
